import { Box } from "@repo/components/src/ui/box";
import React from "react";
import { CHART_COLORS } from "./chartTokens";

export type SparkPoint = { day: string; value: number };

type Props = {
  points: SparkPoint[];
  /** Draws a dashed rule where the campaign's first post landed. */
  campaignStartDay?: string | null;
  secondary?: { points: SparkPoint[]; dashed?: boolean };
};

const WIDTH = 320;
const HEIGHT = 72;

const buildSeries = (series: SparkPoint[]) => {
  const values = series.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  return series.map((p, i) => ({
    x: (i / (series.length - 1)) * WIDTH,
    y: HEIGHT - ((p.value - min) / range) * (HEIGHT - 6) - 3,
  }));
};

const Sparkline = React.memo(
  ({ points, campaignStartDay, secondary }: Props) => {
    if (points.length < 2) return null;

    const primary = buildSeries(points);
    const polyline = primary.map((p) => `${p.x},${p.y}`).join(" ");
    const area = `M ${primary[0]!.x},${HEIGHT} L ${primary
      .map((p) => `${p.x},${p.y}`)
      .join(" L ")} L ${primary[primary.length - 1]!.x},${HEIGHT} Z`;

    const markerX = (() => {
      if (!campaignStartDay) return null;
      const idx = points.findIndex((p) => p.day >= campaignStartDay);
      if (idx < 0) return null;
      return (idx / (points.length - 1)) * WIDTH;
    })();

    const secondaryPolyline =
      secondary && secondary.points.length >= 2
        ? buildSeries(secondary.points)
            .map((p) => `${p.x},${p.y}`)
            .join(" ")
        : null;

    return (
      <Box className="w-full" style={{ height: HEIGHT } as object}>
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          width="100%"
          height={HEIGHT}
          preserveAspectRatio="none"
        >
          <path d={area} fill={CHART_COLORS.primaryFill} stroke="none" />
          <polyline
            fill="none"
            stroke={CHART_COLORS.primary}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            points={polyline}
          />
          {secondaryPolyline ? (
            <polyline
              fill="none"
              stroke={CHART_COLORS.secondary}
              strokeWidth={1.75}
              strokeDasharray={secondary?.dashed ? "4 4" : undefined}
              strokeLinejoin="round"
              strokeLinecap="round"
              points={secondaryPolyline}
            />
          ) : null}
          {markerX !== null ? (
            <line
              x1={markerX}
              x2={markerX}
              y1={0}
              y2={HEIGHT}
              stroke={CHART_COLORS.marker}
              strokeWidth={1}
              strokeDasharray="3 3"
            />
          ) : null}
        </svg>
      </Box>
    );
  },
);

Sparkline.displayName = "Sparkline";

export default Sparkline;
